/**
 * Feedback Analytics Service
 * API calls for video/avatar feedback analytics
 */

import { apiConfig } from '../config/api';

const BASE_URL = apiConfig.brandsApi;

export interface DislikeReason {
  reason: string;
  count: number;
  percentage: number;
}

export interface EntityTypeStats {
  entityType: string;
  likes: number;
  dislikes: number;
  total: number;
}

export interface TrendData {
  date: string;
  likes: number;
  dislikes: number;
}

export interface FeedbackAnalytics {
  totalFeedbacks: number;
  likes: number;
  dislikes: number;
  likeRate: number;
  dislikeReasons: DislikeReason[];
  byEntityType: EntityTypeStats[];
  trend: TrendData[];
}

export type EntityType = 'video' | 'avatar' | 'combination' | '';

export const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: 'video', label: 'Video' },
  { value: 'avatar', label: 'Avatar' },
  { value: 'combination', label: 'Kombin' }
];

export const DISLIKE_REASON_LABELS: Record<string, string> = {
  not_like_me: 'Bana benzemiyor',
  wrong_clothes: 'Kıyafet yanlış görünüyor',
  physical_errors: 'Fiziksel hatalar var',
  low_quality: 'Video kalitesi düşük',
  other: 'Diğer'
};

export const feedbackAnalyticsService = {
  async getAnalytics(
    brandId: string,
    entityType?: EntityType,
    startDate?: string,
    endDate?: string,
    waitingType?: string
  ): Promise<FeedbackAnalytics> {
    const params = new URLSearchParams();
    if (entityType) params.append('entityType', entityType);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (waitingType && waitingType !== 'all') params.append('waitingType', waitingType);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/v1/feedback/analytics/${brandId}${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch feedback analytics: ${response.statusText}`);
    }

    const data = await response.json();
    return data.analytics;
  },

  async getDislikeReasons(
    brandId: string,
    entityType?: EntityType
  ): Promise<DislikeReason[]> {
    const params = new URLSearchParams();
    if (entityType) params.append('entityType', entityType);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/v1/feedback/reasons/${brandId}${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch dislike reasons: ${response.statusText}`);
    }

    const data = await response.json();
    return data.reasons;
  }
};
